import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { POST_STATUS_LABELS, SOCIAL_PLATFORMS } from "@/lib/constants";
import SocialFeed from "@/components/SocialFeed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import SimulatedBanner from "@/components/SimulatedBanner";
import GalleryPicker, { assetUrl } from "@/components/GalleryPicker";
import { useAuth } from "@/context/AuthContext";
import {
  Instagram,
  Facebook,
  Music2,
  MessageCircle,
  Send,
  Clock,
  Save,
  Trash2,
  Upload,
  X,
  Loader2,
  Play,
  Pencil,
  PlugZap,
  Copy,
  Download,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const ICON_MAP = { Instagram, Facebook, Music2, MessageCircle };

const STATUS_STYLES = {
  draft: "bg-stone-100 text-stone-700 border-stone-200",
  scheduled: "bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40",
  published: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30",
  failed: "bg-destructive/10 text-destructive border-destructive/30",
};

const MAX_IMAGE_BYTES = 500 * 1024; // 500 KB para el modo simulado
const MAX_IMAGES = 10; // Fase 24 · máximo de imágenes por carrusel (IG permite hasta 10)
// Fase 24.1 · Plataformas y modos que soportan carrusel/multi-foto.
// - Instagram FEED → carrusel real (INSTAGRAM_CREATE_CAROUSEL_CONTAINER, 2-10 imgs).
// - Instagram STORIES/REELS → 1 imagen/video, no admiten carrusel.
// - Facebook → limitado a 1 imagen: Composio no ofrece tool para posts con
//   `attached_media` (ver `integrations_findings.md`). Sólo Create Photo Post (single)
//   o Upload Photos Batch (a álbum, no feed).
// - WhatsApp/TikTok → single.
const supportsMultiImage = (platformKey, mediaType) => {
  if (platformKey === "instagram") return mediaType === "FEED";
  return false;
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function toLocalInputValue(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function fromLocalInputValue(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function PlatformPage({ platformKey }) {
  const platform = useMemo(() => SOCIAL_PLATFORMS.find((p) => p.key === platformKey), [platformKey]);
  const Icon = ICON_MAP[platform?.icon] || Send;
  const isMessaging = platform?.kind === "message";
  const { hasModule } = useAuth();
  const canPickFromGallery = hasModule("motor_creativo");

  const [connections, setConnections] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  // Composer state
  const emptyForm = {
    content: "",
    images: [], // Fase 24 · array de imágenes (single o carrusel). Se serializa a image_url / image_urls al enviar.
    video_url: "",
    recipients: "",
    connection_id: "",
    scheduled_at: "",
    media_type: "FEED", // Fase 21 · sólo aplica a Instagram (FEED/STORIES/REELS)
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // full post being edited
  const [editOpen, setEditOpen] = useState(false);
  // Fase 24 · métricas reales
  const [insightsFor, setInsightsFor] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false); // Fase 21 · restore visible

  // Fase 20 · ref para forzar refresh del feed tras publicar
  const feedRefreshRef = useRef(null);
  const supportsFeed = platformKey === "facebook" || platformKey === "instagram";
  const realConnection = useMemo(
    () => connections.find((c) => c.mode === "real" && c.composio_account_id),
    [connections]
  );

  // Fase 24 · fetch insights when a post is selected
  useEffect(() => {
    if (!insightsFor) { setInsightsData(null); return; }
    let alive = true;
    setInsightsLoading(true);
    setInsightsData(null);
    (async () => {
      try {
        const { data } = await api.get(`/social/posts/${insightsFor.id}/insights`);
        if (alive) setInsightsData(data);
      } catch (e) {
        if (alive) setInsightsData({ ok: false, error: e.response?.data?.detail || "Error", metrics: {}, comments: [], not_available: ["*"] });
      } finally {
        if (alive) setInsightsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [insightsFor]);

  const load = async () => {
    setLoading(true);
    try {
      const [conns, ps] = await Promise.all([
        api.get(`/social/connections?platform=${platformKey}`),
        api.get(`/social/posts?platform=${platformKey}`),
      ]);
      setConnections(conns.data);
      setPosts(ps.data);
      // Preselect a connection if only one and none selected
      if (conns.data.length && !form.connection_id) {
        setForm((f) => ({ ...f, connection_id: conns.data[0].id }));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar el módulo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [platformKey]);

  // Fase 21 · Cargar borrador autoguardado al montar la plataforma (persistencia)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/social/drafts/${platformKey}`);
        if (!alive || !r.data || !r.data.id) return;
        // Fase 24 · si el draft tiene image_url, lo migramos a images:[url]
        const restoredImages = r.data.image_url ? [r.data.image_url] : [];
        setForm((f) => ({
          ...f,
          content: r.data.content || "",
          images: restoredImages,
          video_url: r.data.video_url || "",
          recipients: r.data.recipients || "",
          connection_id: r.data.connection_id || f.connection_id,
          media_type: r.data.media_type || "FEED",
        }));
        setDraftLoaded(true);
      } catch (_) { /* sin borrador */ }
    })();
    return () => { alive = false; };
  }, [platformKey]);

  // Fase 21 · Autosave debounced (800ms) — evita perder contenido al refrescar
  useEffect(() => {
    if (!form.content && form.images.length === 0 && !form.video_url && !form.recipients) return;
    const t = setTimeout(() => {
      api.put(`/social/drafts/${platformKey}`, {
        platform: platformKey,
        content: form.content,
        // Fase 24 · el draft persiste sólo la primera imagen (compat); al restaurar se rearma como array.
        image_url: form.images[0] || null,
        video_url: form.video_url || null,
        recipients: form.recipients || null,
        connection_id: form.connection_id || null,
        media_type: form.media_type === "FEED" ? null : form.media_type,
      }).catch(() => { /* silencioso, no toast */ });
    }, 800);
    return () => clearTimeout(t);
  }, [platformKey, form.content, form.images, form.video_url, form.recipients, form.connection_id, form.media_type]);

  const filteredPosts = statusFilter === "all"
    ? posts
    : posts.filter((p) => p.status === statusFilter);

  const handleFilesUpload = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    const multiOk = supportsMultiImage(platformKey, form.media_type);
    const toProcess = multiOk ? arr : arr.slice(0, 1);
    const remaining = MAX_IMAGES - form.images.length;
    if (multiOk && arr.length > remaining) {
      toast.message(`Máximo ${MAX_IMAGES} imágenes por carrusel. Se agregarán las primeras ${remaining}.`);
    }
    const limited = toProcess.slice(0, Math.max(0, multiOk ? remaining : 1));
    if (limited.length === 0) return;

    // Fase 25 · Subir a storage (Tigris/local) y usar URL pública.
    // NUNCA enviar data:URI/base64 a Composio (rechazado por el backend).
    const uploaded = [];
    for (const file of limited) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" supera 10 MB`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload/photo?module=composer", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (r.data?.url) uploaded.push(r.data.url);
      } catch (e) {
        toast.error(`Error subiendo ${file.name}: ${formatApiErrorDetail(e.response?.data?.detail) || "falló"}`);
      }
    }
    if (uploaded.length === 0) return;
    setForm((f) => {
      const next = multiOk ? [...f.images, ...uploaded].slice(0, MAX_IMAGES) : [uploaded[0]];
      return { ...f, images: next };
    });
    if (uploaded.length > 0) toast.success(uploaded.length === 1 ? "Imagen subida" : `${uploaded.length} imágenes subidas`);
  };

  const addImageUrl = (url) => {
    if (!url) return;
    const multiOk = supportsMultiImage(platformKey, form.media_type);
    setForm((f) => {
      if (!multiOk) return { ...f, images: [url] };
      if (f.images.includes(url)) return f;
      return { ...f, images: [...f.images, url].slice(0, MAX_IMAGES) };
    });
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const moveImage = (idx, dir) => {
    setForm((f) => {
      const arr = [...f.images];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, images: arr };
    });
  };

  const submitComposer = async (action, e) => {
    e?.preventDefault();
    if (!form.content.trim()) {
      toast.error("El contenido no puede estar vacío");
      return;
    }
    if (isMessaging && action !== "draft" && !form.recipients.trim()) {
      toast.error("Debe indicar destinatarios para WhatsApp");
      return;
    }
    if (action === "schedule" && !form.scheduled_at) {
      toast.error("Debe indicar fecha de programación");
      return;
    }

    setSaving(true);
    try {
      // Fase 24 · derivar image_url / image_urls desde el array `images`.
      // - 1 imagen → image_url (compat single post)
      // - 2+ imágenes → image_urls[] (carrusel IG / multi-foto FB)
      const imgs = Array.isArray(form.images) ? form.images.filter(Boolean) : [];
      const isCarousel = imgs.length >= 2 && supportsMultiImage(platformKey, form.media_type);
      const singleImageUrl = imgs[0] || null;
      // Si IG y hay carrusel, forzamos media_type=CAROUSEL para el backend
      const outboundMediaType = (() => {
        if (platformKey !== "instagram") return null;
        if (isCarousel) return "CAROUSEL";
        return form.media_type !== "FEED" ? form.media_type : null;
      })();
      const payload = {
        platform: platformKey,
        action,
        content: form.content.trim(),
        image_url: isCarousel ? null : singleImageUrl,
        image_urls: isCarousel ? imgs.slice(0, MAX_IMAGES) : null,
        video_url: form.video_url || null,
        recipients: isMessaging ? form.recipients.trim() : null,
        connection_id: form.connection_id || null,
        scheduled_at: action === "schedule" ? fromLocalInputValue(form.scheduled_at) : null,
        media_type: outboundMediaType,
        share_to_feed: platformKey === "instagram" && form.media_type === "REELS" ? true : null,
      };
      // Fase 23 · Idempotency-Key: si el usuario hace doble click / red lenta
      // reintenta, backend devuelve el post original en lugar de duplicar.
      const idemKey =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      await api.post("/social/posts", payload, {
        headers: { "Idempotency-Key": idemKey },
      });
      const successMsg = {
        publish_now: isMessaging ? "Mensaje enviado (simulado)" : "Publicación enviada (simulada)",
        schedule: "Publicación programada",
        draft: "Borrador guardado",
      }[action];
      toast.success(successMsg);
      setForm(emptyForm);
      setDraftLoaded(false);
      // Fase 21 · Al publicar/programar, limpiar el borrador persistido
      if (action !== "draft") {
        try { await api.delete(`/social/drafts/${platformKey}`); } catch (_) { /* silent */ }
      }
      await load();
      // Fase 20 · Auto-refresh del feed real tras publicar
      if (action === "publish_now" && supportsFeed && feedRefreshRef.current) {
        try {
          await api.post(`/social/feed/invalidate?platform=${platformKey}`);
        } catch (_) { /* silent */ }
        feedRefreshRef.current();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const publishNow = async (post) => {
    try {
      await api.post(`/social/posts/${post.id}/publish`);
      toast.success(isMessaging ? "Mensaje enviado (simulado)" : "Publicación enviada (simulada)");
      await load();
      if (supportsFeed && feedRefreshRef.current) {
        try {
          await api.post(`/social/feed/invalidate?platform=${platformKey}`);
        } catch (_) { /* silent */ }
        feedRefreshRef.current();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm("¿Eliminar esta publicación?")) return;
    try {
      await api.delete(`/social/posts/${post.id}`);
      toast.success("Publicación eliminada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const openEdit = (post) => {
    setEditing({
      ...post,
      scheduled_at_local: toLocalInputValue(post.scheduled_at),
      image_url: post.image_url || "",
      recipients: post.recipients || "",
      connection_id: post.connection_id || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/social/posts/${editing.id}`, {
        content: editing.content,
        image_url: editing.image_url || null,
        recipients: isMessaging ? (editing.recipients || null) : null,
        scheduled_at: editing.scheduled_at_local ? fromLocalInputValue(editing.scheduled_at_local) : null,
        connection_id: editing.connection_id || "",
      });
      toast.success("Publicación actualizada");
      setEditOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  if (!platform) return null;

  const actionLabel = isMessaging ? "Enviar ahora" : "Publicar ahora";
  const composerTitle = isMessaging ? "Nuevo mensaje masivo" : "Nueva publicación";
  const contentLabel = isMessaging ? "Mensaje" : "Texto / Caption";

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid={`platform-page-${platform.key}`}>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <span
              className="inline-flex h-10 w-10 rounded-lg items-center justify-center text-white"
              style={{ backgroundColor: platform.accent }}
            >
              <Icon className="h-5 w-5" strokeWidth={1.8} />
            </span>
            {platform.label}
            <SimulatedBanner compact platform={platformKey} />
          </h1>
          <p className="text-sm text-stone-500 mt-2">
            {isMessaging
              ? "Envíe difusiones masivas simuladas y gestione borradores/programaciones."
              : "Cree, programe y publique contenido en su cuenta conectada."}
          </p>
        </div>
      </div>

      <SimulatedBanner platform={platformKey} />

      {/* Cuentas conectadas */}
      <div className="card-elevated p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest font-semibold text-stone-500">
            Cuentas conectadas
          </div>
          <Link
            to="/conectividad"
            className="text-xs text-brand-emerald-dark hover:underline flex items-center gap-1"
            data-testid="link-to-connectivity"
          >
            <PlugZap className="h-3.5 w-3.5" />
            Centro de Conectividad
          </Link>
        </div>
        {connections.length === 0 ? (
          <div className="text-sm text-stone-500">
            No hay cuentas conectadas para {platform.label}. Pida al Master que conecte una desde el Centro de Conectividad.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="connections-list">
            {connections.map((c) => (
              <div
                key={c.id}
                className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm font-medium text-brand-ink flex items-center gap-2"
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: platform.accent }}
                />
                {c.handle}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="card-elevated p-6" data-testid="composer">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-display text-2xl text-brand-ink">{composerTitle}</h2>
        </div>
        <form className="space-y-4" onSubmit={(e) => submitComposer("publish_now", e)}>
          {connections.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                Cuenta
              </Label>
              <Select
                value={form.connection_id}
                onValueChange={(v) => setForm({ ...form, connection_id: v })}
              >
                <SelectTrigger data-testid="composer-connection">
                  <SelectValue placeholder="Seleccione cuenta" />
                </SelectTrigger>
                <SelectContent>
                  {connections.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`composer-connection-${c.id}`}>
                      <span className="font-medium">{c.display_name || c.handle}</span>
                      {c.display_name && c.display_name !== c.handle && (
                        <span className="text-xs text-stone-500 ml-1">· {c.handle}</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Fase 21 · Destino granular para Instagram (Feed / Story / Reel).
              La API de Composio soporta media_type=STORIES|REELS + Feed (sin
              media_type). Facebook y otras plataformas se publican como Feed. */}
          {platformKey === "instagram" && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                Destino de publicación
              </Label>
              <div className="grid grid-cols-3 gap-2" data-testid="composer-media-type">
                {[
                  { key: "FEED", label: "Feed" },
                  { key: "STORIES", label: "Story" },
                  { key: "REELS", label: "Reel" },
                ].map((opt) => {
                  const active = form.media_type === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        // Fase 24 · Story/Reel no soportan multi-foto — recortamos a 1
                        setForm((f) => {
                          const next = { ...f, media_type: opt.key };
                          if (opt.key !== "FEED" && f.images.length > 1) {
                            next.images = f.images.slice(0, 1);
                          }
                          return next;
                        });
                      }}
                      data-testid={`media-type-${opt.key}`}
                      className={[
                        "px-3 py-2 text-sm rounded-md border transition-all",
                        active
                          ? "bg-brand-emerald text-white border-brand-emerald shadow-sm"
                          : "bg-white text-stone-700 border-stone-200 hover:border-brand-emerald/40",
                      ].join(" ")}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-stone-500">
                {form.media_type === "REELS"
                  ? "Reel: requiere URL de video público (mp4). Se compartirá también al Feed."
                  : form.media_type === "STORIES"
                  ? "Story: imagen o video público. Expira en 24h."
                  : "Feed: publicación permanente con imagen."}
              </p>
            </div>
          )}

          {draftLoaded && (
            <div className="text-xs text-brand-emerald-dark bg-brand-emerald-soft/50 border border-brand-emerald/20 rounded-md px-3 py-2 flex items-center gap-2" data-testid="draft-restored">
              <span className="font-semibold">Borrador restaurado</span>
              <span className="text-stone-500">Continúa donde lo dejaste.</span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
              {contentLabel}
            </Label>
            <Textarea
              rows={4}
              placeholder={isMessaging ? "Escriba el mensaje…" : "Escriba su caption…"}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              data-testid="composer-content"
              className="bg-white"
            />
          </div>

          {isMessaging && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                Destinatarios (uno por línea o separados por coma)
              </Label>
              <Textarea
                rows={3}
                placeholder="+50412345678&#10;+50498765432"
                value={form.recipients}
                onChange={(e) => setForm({ ...form, recipients: e.target.value })}
                data-testid="composer-recipients"
                className="bg-white font-mono text-xs"
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                {supportsMultiImage(platformKey, form.media_type)
                  ? `Imágenes (${form.images.length}/${MAX_IMAGES})`
                  : "Imagen (opcional)"}
              </Label>
              {(() => {
                // Fase 24.1 · Si la plataforma no soporta multi y ya hay 1 imagen, ocultamos
                // los controles de "agregar" para no dar la falsa afordancia de "agregar más".
                // El usuario debe quitar la imagen actual antes de poder cargar otra.
                const multi = supportsMultiImage(platformKey, form.media_type);
                const singleFull = !multi && form.images.length >= 1;
                return (
                  <div className="flex gap-2 flex-wrap">
                    {!singleFull && (
                      <>
                        <Input
                          placeholder="Pegar URL de imagen"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && e.currentTarget.value.trim()) {
                              e.preventDefault();
                              addImageUrl(e.currentTarget.value.trim());
                              e.currentTarget.value = "";
                            }
                          }}
                          onBlur={(e) => {
                            const v = e.currentTarget.value.trim();
                            if (v) {
                              addImageUrl(v);
                              e.currentTarget.value = "";
                            }
                          }}
                          data-testid="composer-image-url"
                          className="bg-white flex-1 min-w-[180px]"
                        />
                        <label className="inline-flex items-center gap-1 rounded-md border border-input bg-white px-3 py-2 text-sm cursor-pointer hover:bg-stone-50" data-testid="composer-image-file-label">
                          <Upload className="h-4 w-4" />
                          <input
                            type="file"
                            accept="image/*"
                            multiple={multi}
                            className="hidden"
                            onChange={(e) => {
                              handleFilesUpload(e.target.files);
                              e.target.value = "";
                            }}
                            data-testid="composer-image-file"
                          />
                        </label>
                        <GalleryPicker
                          enabled={canPickFromGallery}
                          testid="composer-gallery-picker"
                          onSelect={(piece) => addImageUrl(assetUrl(piece.file_url))}
                        />
                      </>
                    )}
                    {form.images.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setForm({ ...form, images: [] })}
                        title="Quitar todas"
                        data-testid="composer-images-clear"
                      >
                        <X className="h-4 w-4 mr-1" /> Quitar todo
                      </Button>
                    )}
                  </div>
                );
              })()}
              {/* Fase 24 · Previews multi-imagen con reordenamiento y eliminación */}
              {form.images.length > 0 && (
                <div
                  className="mt-2 flex flex-wrap gap-2"
                  data-testid="composer-images-preview"
                >
                  {form.images.map((src, idx) => (
                    <div
                      key={`${idx}-${src.slice(0, 32)}`}
                      className="relative w-24 h-24 rounded-md border border-border overflow-hidden bg-stone-50 group"
                      data-testid={`composer-image-thumb-${idx}`}
                    >
                      <img
                        src={src}
                        alt={`preview-${idx}`}
                        className="w-full h-full object-cover"
                      />
                      {/* Índice / posición */}
                      <span className="absolute top-1 left-1 text-[10px] font-semibold bg-brand-ink/70 text-white rounded px-1.5 py-0.5">
                        {idx + 1}
                      </span>
                      {/* Botón eliminar */}
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 bg-brand-ink/70 hover:bg-destructive text-white rounded-full p-0.5"
                        aria-label={`Quitar imagen ${idx + 1}`}
                        data-testid={`composer-image-remove-${idx}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      {/* Flechas reordenar (solo si hay carrusel multi-img) */}
                      {form.images.length > 1 && (
                        <div className="absolute bottom-1 left-1 right-1 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={() => moveImage(idx, -1)}
                            disabled={idx === 0}
                            className="bg-brand-ink/70 hover:bg-brand-emerald text-white rounded px-1 text-xs disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label={`Mover imagen ${idx + 1} a la izquierda`}
                            data-testid={`composer-image-move-left-${idx}`}
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            onClick={() => moveImage(idx, 1)}
                            disabled={idx === form.images.length - 1}
                            className="bg-brand-ink/70 hover:bg-brand-emerald text-white rounded px-1 text-xs disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label={`Mover imagen ${idx + 1} a la derecha`}
                            data-testid={`composer-image-move-right-${idx}`}
                          >
                            →
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {form.images.length >= 2 && supportsMultiImage(platformKey, form.media_type) && (
                <p className="text-[11px] text-brand-emerald-dark" data-testid="composer-carousel-hint">
                  Se publicará como CARRUSEL de {form.images.length} imágenes.
                </p>
              )}
              {platformKey === "facebook" && (
                <p className="text-[11px] text-stone-500" data-testid="composer-fb-single-note">
                  Facebook permite 1 imagen por publicación (multi-foto no está disponible en la API de Composio).
                </p>
              )}
            </div>

            {/* Fase 21 · Video URL para IG Story/Reel */}
            {platformKey === "instagram" && form.media_type !== "FEED" && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                  Video URL (mp4 público) {form.media_type === "REELS" && <span className="text-brand-gold-dark">*</span>}
                </Label>
                <Input
                  placeholder="https://…mp4"
                  value={form.video_url}
                  onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  data-testid="composer-video-url"
                  className="bg-white"
                />
                <p className="text-[11px] text-stone-500">
                  Sube el video a un CDN público y pega la URL directa mp4. Instagram debe poder descargarlo.
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                Programar para
              </Label>
              <Input
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                data-testid="composer-scheduled-at"
                className="bg-white"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              type="submit"
              disabled={saving}
              data-testid="composer-publish-now"
              className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold"
            >
              <Send className="h-4 w-4 mr-2" /> {actionLabel}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={(e) => submitComposer("schedule", e)}
              data-testid="composer-schedule"
              className="border-brand-emerald text-brand-emerald-dark hover:bg-brand-emerald-soft"
            >
              <Clock className="h-4 w-4 mr-2" /> Programar
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={saving}
              onClick={(e) => submitComposer("draft", e)}
              data-testid="composer-draft"
            >
              <Save className="h-4 w-4 mr-2" /> Guardar borrador
            </Button>
          </div>
        </form>
      </div>

      {/* Fase 20 · Feed real (solo lectura) para IG/FB */}
      {supportsFeed && realConnection && (
        <SocialFeed
          platform={platformKey}
          connectionId={realConnection.id}
          refreshRef={feedRefreshRef}
        />
      )}

      {/* Posts list */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-display text-2xl text-brand-ink">
            {isMessaging ? "Mensajes" : "Publicaciones"}
          </h2>
          <div className="w-56">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="posts-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(POST_STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="card-elevated p-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="card-elevated p-12 text-center text-stone-500">
            Sin {isMessaging ? "mensajes" : "publicaciones"} todavía.
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPosts.map((p) => (
              <div
                key={p.id}
                className="card-elevated p-5 flex flex-col md:flex-row gap-4"
                data-testid={`post-row-${p.id}`}
              >
                {p.image_url && (
                  <img
                    src={p.image_url}
                    alt=""
                    className="h-24 w-24 md:h-28 md:w-28 object-cover rounded-md border border-border shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Badge className={`border font-semibold ${STATUS_STYLES[p.status] || "bg-stone-100 text-stone-700 border-stone-200"}`}>
                      {POST_STATUS_LABELS[p.status] || p.status}
                    </Badge>
                    {p.mode !== 'real' && <SimulatedBanner compact testid={`post-badge-${p.id}`} platform={platformKey} />}
                    {p.connection_handle && (
                      <span className="text-xs text-stone-500">
                        · {p.connection_handle}
                      </span>
                    )}
                    <span className="text-xs text-stone-400">· por {p.author_name}</span>
                  </div>
                  <div className="text-sm text-brand-ink whitespace-pre-wrap break-words">
                    {p.content}
                  </div>
                  {p.recipients && (
                    <div className="mt-2 text-xs text-stone-500">
                      <span className="font-semibold">Destinatarios:</span>{" "}
                      <span className="font-mono">
                        {p.recipients.split(/\s|,/).filter(Boolean).length} contactos
                      </span>
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-stone-500">
                    <span>Creada: {fmt(p.created_at)}</span>
                    {p.scheduled_at && <span>Programada: {fmt(p.scheduled_at)}</span>}
                    {p.published_at && <span>Publicada: {fmt(p.published_at)}</span>}
                    {p.simulated_post_id && (
                      <span className="font-mono text-brand-emerald-dark">
                        {p.simulated_post_id}
                      </span>
                    )}
                  </div>
                  {p.error && (
                    <div className="mt-2 text-xs text-destructive">Error: {p.error}</div>
                  )}
                </div>
                <div className="flex flex-row md:flex-col items-start gap-1 shrink-0">
                  {/* Fase 21 · Copiar texto + Descargar media (útil para TikTok/Marketplace: subida manual). */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(p.content || "");
                        toast.success("Texto copiado al portapapeles");
                      } catch (_) {
                        toast.error("No se pudo copiar");
                      }
                    }}
                    data-testid={`post-copy-${p.id}`}
                    title="Copiar texto"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  {p.image_url && (
                    <a
                      href={p.image_url}
                      download
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-stone-100 text-stone-600"
                      data-testid={`post-download-${p.id}`}
                      title="Descargar media"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  )}
                  {p.status === "published" && p.mode === "real" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setInsightsFor(p)}
                      data-testid={`post-insights-${p.id}`}
                      title="Ver métricas reales"
                      className="text-brand-emerald-dark hover:bg-brand-emerald-soft"
                    >
                      <BarChart3 className="h-4 w-4" />
                    </Button>
                  )}
                  {p.status !== "published" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(p)}
                      data-testid={`post-edit-${p.id}`}
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {(p.status === "draft" || p.status === "scheduled" || p.status === "failed") && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => publishNow(p)}
                      data-testid={`post-publish-${p.id}`}
                      className="text-brand-emerald-dark hover:bg-brand-emerald-soft"
                      title={actionLabel}
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deletePost(p)}
                    data-testid={`post-delete-${p.id}`}
                    className="text-destructive hover:bg-destructive/10"
                    title="Eliminar"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={(v) => { if (!v) { setEditOpen(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="edit-post-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              Editar {isMessaging ? "mensaje" : "publicación"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                  {contentLabel}
                </Label>
                <Textarea
                  rows={4}
                  value={editing.content}
                  onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  data-testid="edit-content"
                />
              </div>
              {isMessaging && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                    Destinatarios
                  </Label>
                  <Textarea
                    rows={3}
                    value={editing.recipients}
                    onChange={(e) => setEditing({ ...editing, recipients: e.target.value })}
                    className="font-mono text-xs"
                  />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                    Imagen (URL)
                  </Label>
                  <Input
                    value={editing.image_url}
                    onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                    Programar para
                  </Label>
                  <Input
                    type="datetime-local"
                    value={editing.scheduled_at_local || ""}
                    onChange={(e) => setEditing({ ...editing, scheduled_at_local: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditOpen(false); setEditing(null); }}>
              Cancelar
            </Button>
            <Button
              onClick={saveEdit}
              disabled={saving}
              data-testid="edit-post-save"
              className="bg-brand-emerald text-white hover:bg-brand-emerald-dark"
            >
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fase 24 · Modal de métricas reales por publicación */}
      <Dialog open={!!insightsFor} onOpenChange={(v) => !v && setInsightsFor(null)}>
        <DialogContent className="max-w-lg" data-testid="insights-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Métricas reales de la publicación</DialogTitle>
            <DialogDescription>
              Datos consultados en vivo desde {platformKey === "instagram" ? "Instagram Graph API" : "Facebook Graph API"} vía Composio.
            </DialogDescription>
          </DialogHeader>
          {insightsLoading ? (
            <div className="p-6 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
          ) : !insightsData ? null : !insightsData.ok ? (
            <div className="p-4 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
              {insightsData.error}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(insightsData.metrics || {}).map(([k, v]) => (
                  <div key={k} className="card-elevated p-3 text-center" data-testid={`metric-${k}`}>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{k}</div>
                    <div className="font-serif-display text-2xl text-brand-ink mt-1">{typeof v === "number" ? v.toLocaleString() : String(v)}</div>
                  </div>
                ))}
                {Object.keys(insightsData.metrics || {}).length === 0 && (
                  <div className="col-span-full text-sm text-stone-500 text-center py-4">Sin métricas disponibles.</div>
                )}
              </div>
              {insightsData.not_available && insightsData.not_available.length > 0 && (
                <div className="text-xs text-stone-500 border-t border-stone-100 pt-3">
                  <strong>No disponibles en esta API:</strong> {insightsData.not_available.join(", ")}
                  {insightsData.note && <div className="mt-1 italic">{insightsData.note}</div>}
                </div>
              )}
              {insightsData.comments && insightsData.comments.length > 0 && (
                <div className="pt-3 border-t border-stone-100">
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2">Últimos comentarios</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {insightsData.comments.map((c) => (
                      <div key={c.id} className="text-sm border-b border-stone-100 pb-2">
                        <strong className="text-brand-ink">{c.username || "anónimo"}:</strong> <span className="text-stone-700">{c.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
